import React, { useEffect, useState } from "react";
import { motion, useScroll, useSpring, useMotionValueEvent, useReducedMotion } from "framer-motion";
import Header from "../../components/Header";
import PageBanner from "../../components/PageBanner";
import Footer from "../../components/Footer";
import "../home/homeRedesign.css"; // shared primitives
import "../privacy/privacy.css"; // shared legal-page design (pp- classes)

const EASE = [0.23, 1, 0.32, 1];

const Rise = ({ children, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, transform: "translateY(24px)" }}
    whileInView={{ opacity: 1, transform: "translateY(0px)" }}
    viewport={{ once: true, margin: "-70px" }}
    transition={{ duration: 0.5, ease: EASE }}
  >
    {children}
  </motion.div>
);

const IconCheck = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
);
const IconMail = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8" /><path d="M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
);

const LegalPage = ({ data, helpEmail = "info@ncetmail.com", bannerClassName = "", defaultDescription = "" }) => {
  const { hero, sections } = data;
  const [active, setActive] = useState(sections[0].id);
  const [pct, setPct] = useState(0);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });
  useMotionValueEvent(scrollYProgress, "change", (v) => setPct(Math.round(v * 100)));

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Active-section tracking driven by scroll position, with a bottom clamp so
  // the trailing sections (which can't scroll high enough) still get marked
  // done — keeping the stepper in sync with the reading percentage.
  useEffect(() => {
    const onScroll = () => {
      const scrollY = window.scrollY;
      const vh = window.innerHeight;
      const docH = document.documentElement.scrollHeight;
      // at (or near) the bottom → last section is active
      if (scrollY + vh >= docH - 6) {
        setActive(sections[sections.length - 1].id);
        return;
      }
      const line = scrollY + 160; // a heading becomes active once it passes here
      let current = sections[0].id;
      for (const s of sections) {
        const el = document.getElementById(s.id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top + scrollY;
        if (top <= line) current = s.id; else break;
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [sections]);

  const activeIndex = Math.max(0, sections.findIndex((s) => s.id === active));
  const fillPct = sections.length > 1 ? (activeIndex / (sections.length - 1)) * 100 : 0;

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.pageYOffset - 104;
    window.scrollTo({ top: y, behavior: reduce ? "auto" : "smooth" });
  };

  const titleParts = hero.title.split(" ");
  const titleLast = titleParts.pop();

  // Hero copy: use an admin-set description (or a per-page default) to fill the
  // banner's left column. When a description is present, the "Last Updated" and
  // section count move into pill "facts"; otherwise the subtitle keeps showing
  // the last-updated line (unchanged for pages that pass no description).
  const heroDescription = hero.description || defaultDescription;
  const heroFacts = heroDescription
    ? [
        hero.lastUpdated ? `Last Updated: ${hero.lastUpdated}` : null,
        sections?.length ? `${sections.length} Section${sections.length === 1 ? "" : "s"}` : null,
      ].filter(Boolean)
    : [];

  return (
    <div className="pp-page">
      <Header topbarEnable="enable" />

      <motion.div className="pp-progress" style={{ scaleX: progress }} aria-hidden="true" />

      {/* ---- Hero (shared PageBanner) ---- */}
      <PageBanner
        title={hero.title}
        eyebrow={hero.eyebrow}
        subtitle={heroDescription || (hero.lastUpdated ? `Last Updated: ${hero.lastUpdated}` : undefined)}
        facts={heroFacts}
        className={bannerClassName}
      />

      {/* ---- Body ---- */}
      <section className="pp-body">
        <div className="container">
          <div className="pp-layout">
            {/* Progress stepper TOC */}
            <aside className="pp-toc">
              <div className="pp-toc__inner">
                <div className="pp-toc__top">
                  <span className="pp-toc__label">On this page</span>
                  <span className="pp-toc__pct">{pct}<i>%</i></span>
                </div>

                <div className="pp-toc__steps">
                  <span className="pp-toc__track" aria-hidden="true" />
                  <span className="pp-toc__fill" style={{ height: `${fillPct}%` }} aria-hidden="true" />
                  <ul>
                    {sections.map((s, i) => (
                      <li key={s.id}>
                        <button
                          type="button"
                          className={`pp-step${active === s.id ? " is-active" : ""}${i < activeIndex ? " is-done" : ""}`}
                          onClick={() => scrollTo(s.id)}
                        >
                          <span className="pp-step__node">{i < activeIndex ? <IconCheck /> : s.no}</span>
                          <span className="pp-step__txt">{s.title}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                <a className="pp-toc__help" href={`mailto:${helpEmail}`}>
                  <span className="pp-toc__help-ic"><IconMail /></span>
                  <span className="pp-toc__help-tx">Questions about this page?<br /><strong>Email our team</strong></span>
                </a>
              </div>
            </aside>

            {/* Sections */}
            <div className="pp-content">
              {sections.map((s) => (
                <Rise key={s.id} className="pp-sec">
                  <article id={s.id} className={`pp-sec__card${active === s.id ? " is-active" : ""}`}>
                    <header className="pp-sec__head">
                      <span className="pp-sec__no">{s.no}</span>
                      <h2 className="pp-sec__title">{s.title}</h2>
                    </header>
                    {s.blocks.map((b, i) => {
                      if (b.type === "p") return <p className="pp-p" key={i}>{b.text}</p>;
                      if (b.type === "list") {
                        return (
                          <ul className="pp-list" key={i}>
                            {b.items.map((it, j) => (
                              <li key={j}>
                                <span className="pp-list__mark"><IconCheck /></span>
                                <span className="pp-list__txt">
                                  {it.term ? <><strong>{it.term}:</strong> {it.text}</> : it.text}
                                </span>
                              </li>
                            ))}
                          </ul>
                        );
                      }
                      if (b.type === "contact") {
                        return (
                          <div className="pp-contact" key={i}>
                            {b.items.map((c, j) => (
                              <div className="pp-contact__row" key={j}>
                                <span className="pp-contact__label">{c.label}</span>
                                {c.href
                                  ? <a className="pp-contact__value" href={c.href}>{c.value}</a>
                                  : <span className="pp-contact__value">{c.value}</span>}
                              </div>
                            ))}
                          </div>
                        );
                      }
                      return null;
                    })}
                  </article>
                </Rise>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LegalPage;
